import React from 'react';
import { Typography, Box } from '@mui/material';
import { useTheme } from '../../context/ThemeContext';
import { formatCurrency } from '../../config/currency';
import type { RevenueChannel } from '../../api/financeApi';

export interface RevenueBreakdownFormProps {
  channels: RevenueChannel[];
  values: Record<number, string>; // channelId → gross amount string
  onChange: (channelId: number, grossAmount: string) => void;
  disabled?: boolean;
}

const RevenueBreakdownForm: React.FC<RevenueBreakdownFormProps> = ({
  channels,
  values,
  onChange,
  disabled = false,
}) => {
  const { theme } = useTheme();

  const sorted = Array.isArray(channels)
    ? [...channels].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
    : [];

  let totalGross = 0;
  sorted.forEach((ch) => {
    const gross = parseFloat(values[ch.id] || '') || 0;
    totalGross += gross;
  });

  const hasNegative = sorted.some((ch) => {
    const val = parseFloat(values[ch.id] || '') || 0;
    return val < 0;
  });

  const handleChange = (channelId: number, raw: string) => {
    const sanitized = raw.replace(/[^0-9.-]/g, '').replace(/(\..*?)\..*/g, '$1');
    onChange(channelId, sanitized);
  };

  const inputBaseStyle = (isNeg: boolean): React.CSSProperties => ({
    width: 140,
    boxSizing: 'border-box',
    padding: '8px 12px',
    fontSize: 15,
    fontWeight: 600,
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    background: disabled
      ? (theme.components?.input?.disabled || theme.gray[50])
      : (theme.components?.input?.background || '#F0EDE8'),
    border: `1px solid ${isNeg ? theme.danger : (theme.components?.input?.border || '#E8E6E1')}`,
    borderRadius: 8,
    color: theme.text,
    outline: 'none',
    minHeight: 40,
    textAlign: 'right',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  });

  const metaStyle: React.CSSProperties = {
    fontSize: 11,
    color: theme.gray[500],
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
  };

  return (
    <Box data-testid="revenue-breakdown-form" sx={{ mb: 2 }}>
      {sorted.map((channel) => {
        const grossVal = parseFloat(values[channel.id] || '') || 0;
        const isNeg = grossVal < 0;

        return (
          <Box
            key={channel.id}
            data-testid={`channel-row-${channel.id}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              px: 2,
              py: 1.25,
              mb: 1,
              background: theme.components?.card?.background || '#FFFFFF',
              border: `1px solid ${theme.components?.card?.border || '#E8E6E1'}`,
              borderRadius: '12px',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                  fontWeight: 600,
                  fontSize: 15,
                  color: theme.text,
                  lineHeight: 1.2,
                }}
              >
                {channel.name}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <input
                id={`channel-${channel.id}`}
                type="text"
                inputMode="decimal"
                placeholder="0.00"
                value={values[channel.id] || ''}
                onChange={(e) => handleChange(channel.id, e.target.value)}
                disabled={disabled}
                aria-label={`${channel.name} gross revenue`}
                style={inputBaseStyle(isNeg)}
                onFocus={(e) => {
                  if (!disabled) {
                    e.currentTarget.style.borderColor =
                      theme.components?.input?.focus || theme.primary;
                    e.currentTarget.style.boxShadow =
                      theme.components?.input?.focusRing || '0 0 0 3px color-mix(in srgb, var(--action) 20%, transparent)';
                  }
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = isNeg
                    ? theme.danger
                    : (theme.components?.input?.border || '#E8E6E1');
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
              {isNeg && (
                <span style={{ ...metaStyle, color: theme.danger, marginTop: 4 }} role="alert">
                  Cannot be negative
                </span>
              )}
            </Box>
          </Box>
        );
      })}

      {/* Total row */}
      <Box
        data-testid="channel-totals"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
          fontWeight: 600,
          fontSize: 15,
          color: theme.text,
          px: 2,
          py: 1,
        }}
      >
        <span>Total: {formatCurrency(totalGross)}</span>
      </Box>

      {hasNegative && (
        <Typography
          role="alert"
          sx={{
            color: theme.danger,
            fontSize: 13,
            mt: 1,
            px: 2,
            fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
          }}
        >
          Please correct negative amounts before submitting.
        </Typography>
      )}
    </Box>
  );
};

export default RevenueBreakdownForm;
